import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { alpha } from "@mui/material/styles";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";

/**
 * The application dialog shell. Feature components own their content only;
 * sizing, focus behaviour, scrolling and the title/action chrome live here.
 */
export default function AppDialog({
  open,
  onClose,
  title,
  subtitle,
  children,
  actions,
  loading = false,
  closeButton = true,
  maxWidth = "md",
  fullWidth = true,
  fullScreen = false,
  contentSx,
  titleSx,
  actionsSx,
  PaperProps,
  disableBackdropClose = false,
  keepMounted = false,
  ...dialogProps
}) {
  const handleClose = (event, reason) => {
    if (loading || (disableBackdropClose && (reason === "backdropClick" || reason === "escapeKeyDown"))) return;
    onClose?.(event, reason);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      fullScreen={fullScreen}
      keepMounted={keepMounted}
      PaperProps={{
        ...PaperProps,
        sx: {
          overflow: "hidden",
          ...PaperProps?.sx,
        },
      }}
      {...dialogProps}
    >
      {(title || subtitle || closeButton) && (
        <DialogTitle component="div" sx={{ pr: 7, ...titleSx }}>
          <Stack spacing={subtitle ? 0.35 : 0}>
            {title && <Typography variant="h6" component="h2">{title}</Typography>}
            {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
          </Stack>
          {closeButton && (
            <IconButton
              aria-label="Close dialog"
              onClick={(event) => handleClose(event, "closeButton")}
              disabled={loading}
              size="small"
              sx={{ position: "absolute", right: 16, top: 16 }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          )}
        </DialogTitle>
      )}

      <DialogContent dividers sx={{ ...contentSx }}>
        {children}
      </DialogContent>

      {actions && (
        <DialogActions sx={{ ...actionsSx }}>
          {loading && <CircularProgress size={18} sx={{ mr: "auto" }} />}
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
}

export function DialogSection({ children, sx }) {
  return <Box sx={{ p: 2, borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.025), ...sx }}>{children}</Box>;
}
